import React, { useCallback, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import ScheduleTask from '@totem/components/ticketing/details/tasks/scheduleTask';
import TaskContext from '@totem/components/ticketing/details/tasks/taskContext';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import {
  HierarchicalTask,
  Resources,
  Task,
  TASK_TYPE_GROUP,
  TaskPositionUpdate,
  TaskPositionUpdateRequest,
} from '@totem/components/ticketing/ticketing';
import {
  buildTaskHierarchy,
  createTaskMap,
  getDefaultTaskWorkflowSteps,
  getNextRootPosition,
  getNextTask,
  getPreviousTask,
  sortTasksForUIDisplay,
} from '@totem/components/ticketing/utilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import {
  sortNumberAscending,
  sortNumberDescending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';
type Props = {
  children?: React.ReactNode;
};
const TaskContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { ticket, onAction } = useContext(TicketContext);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [taskAction, setTaskAction] = useState<string>('');
  const [taskActionExtras, setTaskActionExtras] = useState<string>('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activeTasks, setActiveTasks] = useState<Task[]>([]);
  const [taskHierarchy, setTaskHierarchy] = useState<HierarchicalTask[]>([]);
  const [taskMap, setTaskMap] = useState<Map<number, HierarchicalTask>>(
    new Map<number, HierarchicalTask>(),
  );
  const [resources, setResources] = useState<Resources>([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<Task>(null);
  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.tasks)
    ) {
      const newTasks = ticket.ticket.tasks
        .filter((chk) => !chk.isDeleted)
        .sort((compA, compB) =>
          sortNumberAscending(compA.number, compB.number),
        );
      const newActiveTask = sortTasksForUIDisplay(newTasks);
      const newTaskHierarchy = buildTaskHierarchy(newActiveTask);
      const newTaskMap = createTaskMap(newActiveTask);
      setTasks(newTasks);

      setActiveTasks(newActiveTask);

      setTaskHierarchy(newTaskHierarchy);

      setTaskMap(newTaskMap);
    } else {
      setTasks([]);

      setActiveTasks([]);

      setTaskHierarchy([]);
    }
  }, [ticket?.ticket?.tasks]);

  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.partiesInvolved)
    ) {
      const res = ticket.ticket.partiesInvolved
        .map((pi) => {
          return {
            id: pi.id,
            name: pi.name,
            email: pi.email,
          };
        })
        .sort((compA, compB) => sortStringAscending(compA.name, compB.name));
      setResources(res);
    } else {
      setResources([]);
    }
  }, [ticket?.ticket?.partiesInvolved]);
  const sendTask = useCallback(
    async (paramTicketId: string, payload: Task) => {
      if (!isSending) {
        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/tasks`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            onAction('refresh', '');
          }
        });
      }
    }, // End async
    [],
  );
  const sendTaskPositionUpdate = useCallback(
    async (paramTicketId: string, updates: TaskPositionUpdate[]) => {
      if (!isSending) {
        const payload: TaskPositionUpdateRequest = {
          ticketId: paramTicketId,
          updates,
        };

        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/tasks/positionUpdates`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            onAction('refresh', '');
          }
        });
      }
    }, // End async
    [],
  );
  const sendTaskDelete = useCallback(
    async (paramTicketId: string, paramTaskId: string) => {
      if (!isSending) {
        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/tasks/${paramTaskId}`, {
          method: 'DELETE',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            onAction('refresh', '');
          }
        });
      }
    }, // End async
    [],
  );
  const getPrevious = (taskNumber: number) => {
    return getPreviousTask(taskNumber, taskMap, tasks);
  };
  const getNext = (taskNumber: number) => {
    return getNextTask(taskNumber, taskMap, tasks);
  };
  const getTask = (taskNumber: number): Task => {
    const tsk = activeTasks.find((task) => task.number === taskNumber);
    if (isNotNull(tsk)) {
      return tsk;
    }
    return null;
  };
  const getPreviousSiblings = (refTask: Task): Task[] => {
    if (isNotNull(refTask)) {
      const previousSiblings = activeTasks
        .filter(
          (chk) =>
            chk.parent === refTask.parent && chk.position < refTask.position,
        )
        .sort((compA, compB) =>
          sortNumberDescending(compA.position, compB.position),
        );
      return previousSiblings;
    }
    return [];
  };
  const getPreviousSibling = (refTask: Task): Task => {
    if (isNotNull(refTask)) {
      const previousSiblings = getPreviousSiblings(refTask);
      if (previousSiblings.length > 0) {
        return previousSiblings[0];
      }
    }
    return null;
  };
  const getNextSiblings = (refTask: Task): Task[] => {
    if (isNotNull(refTask)) {
      const nextSiblings = activeTasks
        .filter(
          (chk) =>
            chk.parent === refTask.parent && chk.position > refTask.position,
        )
        .sort((compA, compB) =>
          sortNumberAscending(compA.position, compB.position),
        );
      return nextSiblings;
    }
    return [];
  };
  const getNextSibling = (refTask: Task): Task => {
    if (isNotNull(refTask)) {
      const nextSiblings = getNextSiblings(refTask);
      if (nextSiblings.length > 0) {
        return nextSiblings[0];
      }
    }
    return null;
  };
  const getNextPositionByParent = (parent: number): number => {
    const childTasks = activeTasks
      .filter((chk) => chk.parent === parent)
      .sort((compA, compB) =>
        sortNumberDescending(compA.position, compB.position),
      );
    if (isNotNull(childTasks) && childTasks.length > 0) {
      return childTasks[0].position + 1;
    }
    return 0;
  };
  const getPadding = (depth: number): string => {
    let padding = '';
    for (let idx = 0; idx < depth; idx++) {
      padding = `${padding}-`;
    }
    if (padding !== '') {
      padding = `${padding}>`;
    }
    return padding;
  };
  const dumpHierarchy = (taskLevel: HierarchicalTask[], depth: number) => {
    if (isNotNull(taskLevel)) {
      taskLevel.forEach((taskItem) => {
        const previous = getPreviousSibling(taskItem);
        const next = getNextSibling(taskItem);
        const nextDisplay = isNotNull(next) ? next.number : 'null';
        const previousDisplay = isNotNull(previous) ? previous.number : 'null';

        logger.debug(
          `${getPadding(depth)} Task: ${taskItem.number}(Parent: ${
            taskItem.parent
          } / Pos: ${taskItem.position}) [${previousDisplay}:${nextDisplay}]`,
        );
        if (isNotNull(taskItem.children)) {
          dumpHierarchy(taskItem.children, depth + 1);
        }
      });
    }
  };

  const handleStatusChange = (newStatus: string, refTask: Task) => {
    switch (newStatus) {
      case 'SCHEDULED':
        setSelectedTask(refTask);

        setShowScheduleDialog(true);
        break;
      case 'IN PROGRESS':
        {
          const startDate = dayjs().toISOString();
          const payload = {
            ...refTask,
            status: newStatus,
            actualStartDate: startDate,
          };
          sendTask(ticket.ticket.id, payload);
        }
        break;
      case 'COMPLETED':
        {
          const endDate = dayjs().toISOString();
          const payload = {
            ...refTask,
            status: newStatus,
            actualEndDate: endDate,
          };
          sendTask(ticket.ticket.id, payload);
        }
        break;
      default:
        {
          const payload = { ...refTask, status: newStatus };
          sendTask(ticket.ticket.id, payload);
        }
        break;
    }
  };
  const handleAction = (newAction: string, newExtras: string) => {
    switch (newAction) {
      case 'dump-hierarchy':
        dumpHierarchy(taskHierarchy, 0);
        break;
      case 'do-task-delete':
        if (isNotNull(newExtras)) {
          try {
            const extras: Task = JSON.parse(newExtras);

            if (isNotNull(extras)) {
              const taskId = extras.id;
              const ticketId = ticket.ticket.id;
              sendTaskDelete(ticketId, taskId);
            }
          } catch {
            onAction('', '');
          }
        }
        break;
      case 'move-task-left':
        if (isNotNull(newExtras)) {
          try {
            const extras: Task = JSON.parse(newExtras);

            if (isNotNull(extras) && extras.parent !== 0) {
              const parent = getTask(extras.parent);
              const parentsNextSiblings = getNextSiblings(parent);
              const positionUpdates = parentsNextSiblings.map((chk) => {
                return {
                  id: chk.id,
                  parent: chk.parent,
                  position: chk.position + 1,
                };
              });
              const positionUpdate: TaskPositionUpdate = {
                id: extras.id,
                parent: parent.parent,
                position: parent.position + 1,
              };
              positionUpdates.push(positionUpdate);

              sendTaskPositionUpdate(ticket.ticket.id, positionUpdates);
            }
          } catch {
            onAction('', '');
          }
        }
        break;
      case 'move-task-right':
        if (isNotNull(newExtras)) {
          try {
            const extras: Task = JSON.parse(newExtras);

            if (isNotNull(extras)) {
              const previousTask = getPreviousSibling(extras);

              if (previousTask.parent === extras.parent) {
                if (previousTask.type === TASK_TYPE_GROUP) {
                  const newPosition = getNextPositionByParent(
                    previousTask.number,
                  );
                  const positionUpdate: TaskPositionUpdate = {
                    id: extras.id,
                    parent: previousTask.number,
                    position: newPosition,
                  };
                  sendTaskPositionUpdate(ticket.ticket.id, [positionUpdate]);
                }
              }
            }
          } catch {
            onAction('', '');
          }
        }
        break;
      case 'move-task-down':
        if (isNotNull(newExtras)) {
          try {
            const extras: Task = JSON.parse(newExtras);

            if (isNotNull(extras)) {
              const nextTask = getNextSibling(extras);

              if (nextTask.parent === extras.parent) {
                const positionUpdates = [
                  {
                    id: extras.id,
                    parent: extras.parent,
                    position: nextTask.position,
                  },
                  {
                    id: nextTask.id,
                    parent: nextTask.parent,
                    position: extras.position,
                  },
                ];
                sendTaskPositionUpdate(ticket.ticket.id, positionUpdates);
              }
            }
          } catch {
            onAction('', '');
          }
        }
        break;
      case 'move-task-up':
        if (isNotNull(newExtras)) {
          try {
            const extras: Task = JSON.parse(newExtras);

            if (isNotNull(extras)) {
              const previousTask = getPreviousSibling(extras);

              if (previousTask.parent === extras.parent) {
                const positionUpdates = [
                  {
                    id: extras.id,
                    parent: extras.parent,
                    position: previousTask.position,
                  },
                  {
                    id: previousTask.id,
                    parent: previousTask.parent,
                    position: extras.position,
                  },
                ];
                sendTaskPositionUpdate(ticket.ticket.id, positionUpdates);
              }
            }
          } catch {
            onAction('', '');
          }
        }
        break;
      case 'move-task-dnd':
        if (isNotNull(newExtras)) {
          try {
            const extras: {
              draggedTaskNumber: number;
              targetTaskNumber: number;
              position: 'above' | 'below' | 'inside';
            } = JSON.parse(newExtras);
            const draggedTask = getTask(extras.draggedTaskNumber);
            const targetTask = getTask(extras.targetTaskNumber);
            // Ensure both tasks exist and we are not trying to drop a task inside a non-group
            if (
              !isNotNull(draggedTask) ||
              !isNotNull(targetTask) ||
              draggedTask.id === targetTask.id ||
              (extras.position === 'inside' &&
                targetTask.type !== TASK_TYPE_GROUP)
            ) {
              break;
            }
            // Prevent moving a task into one of its own descendants
            let parentWalker =
              extras.position === 'inside'
                ? targetTask.number
                : targetTask.parent;
            while (parentWalker !== 0) {
              if (parentWalker === draggedTask.number) {
                return;
              }
              const parentTask = getTask(parentWalker);
              if (!isNotNull(parentTask)) {
                break;
              }
              parentWalker = parentTask.parent;
            }
            const destinationParent =
              extras.position === 'inside'
                ? targetTask.number
                : targetTask.parent;
            const resequence = (taskList: Task[], parent: number) =>
              taskList.map((tsk, idx) => ({
                id: tsk.id,
                parent,
                position: idx,
              }));
            const destinationSiblings = activeTasks
              .filter(
                (chk) =>
                  chk.parent === destinationParent && chk.id !== draggedTask.id,
              )
              .sort((compA, compB) =>
                sortNumberAscending(compA.position, compB.position),
              );
            const targetIndex = destinationSiblings.findIndex(
              (chk) => chk.number === targetTask.number,
            );
            let insertIndex = destinationSiblings.length;
            if (extras.position === 'above' && targetIndex >= 0) {
              insertIndex = targetIndex;
            } else if (extras.position === 'below' && targetIndex >= 0) {
              insertIndex = targetIndex + 1;
            }
            const destinationWithDragged = [...destinationSiblings];
            destinationWithDragged.splice(insertIndex, 0, {
              ...draggedTask,
              parent: destinationParent,
            });
            const updates: TaskPositionUpdate[] = [];
            updates.push(
              ...resequence(destinationWithDragged, destinationParent),
            );
            if (draggedTask.parent !== destinationParent) {
              const sourceSiblings = activeTasks
                .filter(
                  (chk) =>
                    chk.parent === draggedTask.parent &&
                    chk.id !== draggedTask.id,
                )
                .sort((compA, compB) =>
                  sortNumberAscending(compA.position, compB.position),
                );
              updates.push(...resequence(sourceSiblings, draggedTask.parent));
            }
            if (updates.length > 0) {
              sendTaskPositionUpdate(ticket.ticket.id, updates);
            }
          } catch {
            onAction('', '');
          }
        }
        break;
      default:
        setTaskAction(newAction);

        setTaskActionExtras(newExtras);
    }
  };
  return (
    <TaskContext.Provider
      value={{
        taskAction,
        taskActionExtras,
        onTaskAction: handleAction,
        rawTasks: tasks,
        tasks: activeTasks,
        taskHierarchy,
        taskMap,
        resources,
        taskWorkflow: getDefaultTaskWorkflowSteps(),
        getTask,
        getPreviousSibling,
        getPreviousSiblings,
        getNextSibling,
        getNextSiblings,
        getPreviousTask: getPrevious,
        getNextTask: getNext,
        getNextRootPosition: () => getNextRootPosition(ticket),
        getNextPositionByParent,
        onTaskStatusChange: handleStatusChange,
      }}
    >
      {children}
      {showScheduleDialog && (
        <ScheduleTask
          visible={showScheduleDialog}
          task={selectedTask}
          onCancel={() => {
            setShowScheduleDialog(false);
          }}
          onComplete={(newStartDate) => {
            setShowScheduleDialog(false);
            const payload = {
              ...selectedTask,
              status: 'SCHEDULED',
              expectedStartDate: newStartDate,
            };
            sendTask(ticket.ticket.id, payload);
          }}
        />
      )}
    </TaskContext.Provider>
  );
};
export default TaskContainer;
